'use client'
import { CMSLink } from '@/components/Link'
import { Media } from '@/components/Media'
import RichText from '@/components/RichText'
import { ChevronsRight } from 'lucide-react'
import type { AboutBlock2 as AboutBlock2Type } from '@/payload-types'

export const AboutBlock2 = (props: AboutBlock2Type) => {
  const { eyebrow, title, description, images, links, imageAlignment = 'right' } = props

  // Determine order classes based on image alignment
  const contentOrder = imageAlignment === 'left' ? 'order-2 lg:order-2' : 'order-2 lg:order-1'
  const imageOrder = imageAlignment === 'left' ? 'order-1 lg:order-1' : 'order-1 lg:order-2'

  return (
    <section className="container mx-auto pt-5" aria-labelledby="aboutblock-title">
      <div className="flex flex-col lg:flex-row items-start lg:items-center gap-5">
        {/* Content Section */}
        <section
          className={`w-full lg:flex-1 flex flex-col lg:justify-center space-y-4 ${contentOrder}`}
        >
          {/* Header */}
          <header className="text-left space-y-5">
            {eyebrow && (
              <div className="border border-primary px-4 py-1 text-xs font-medium rounded-full w-fit">
                <span className="text-primary">{eyebrow}</span>
              </div>
            )}
            {title && (
              <div id="aboutblock-title" className="text-left">
                <RichText
                  data={title}
                  enableGutter={false}
                  enableProse={false}
                  className="text-2xl md:text-3xl lg:text-4xl font-bold text-left mb-4 lg:max-w-3xl"
                />
              </div>
            )}
          </header>

          {/* Description */}
          {description && (
            <div className="text-muted-foreground">
              <RichText
                data={description}
                enableGutter={false}
                enableProse={false}
                className="text-lg md:text-xl text-muted-foreground max-w-3xl"
              />
            </div>
          )}

          {/* Action Links */}
          {links && links.length > 0 && (
            <nav aria-label="About actions" className="pt-2">
              <ul className="flex flex-wrap gap-4" role="list">
                {links.map((link, i: number) => (
                  <li key={i}>
                    <CMSLink
                      {...link.link}
                      className="inline-flex items-center gap-3 border border-primary bg-primary text-primary-foreground hover:bg-primary/90 font-semibold text-base px-6 py-8 rounded-full transition-colors"
                      aria-label={'Learn more about this service'}
                    >
                      <span className="w-9 h-9 flex items-center justify-center bg-primary text-primary-foreground rounded-full">
                        <ChevronsRight size={18} />
                      </span>
                    </CMSLink>
                  </li>
                ))}
              </ul>
            </nav>
          )}
        </section>

        {/* Image Section */}
        <figure className={`w-full lg:w-1/2 rounded-3xl overflow-hidden ${imageOrder}`}>
          {images?.[0]?.image && (
            <Media
              resource={images[0].image}
              alt={images[0]?.caption || 'About'}
              imgClassName="w-full aspect-[3/2] sm:aspect-[4/3] md:aspect-[3/2] lg:aspect-[3/2] xl:aspect-[3/2] 2xl:aspect-[3/2] object-cover rounded-3xl"
              priority={false}
              quality={75}
              loading="lazy"
              fetchPriority="low"
              sizes="(max-width: 640px) 100vw, (max-width: 768px) 100vw, (max-width: 1024px) 50vw, 40vw"
            />
          )}
        </figure>
      </div>
    </section>
  )
}
